/**
 * Conversion from the design's stitch sequence (mm, floating point) to the machine coordinate
 * system used by all formats: integer units of 0.1 mm, y axis pointing down, origin at the
 * export origin (hoop centre). Positions are rounded from absolute coordinates, so rounding
 * errors never accumulate. Stitches and jumps that exceed a format's per-command displacement
 * limit are subdivided here, per format.
 */
import { CMD, type Command, type StitchSequence } from '../engine/stitch/sequence';
import type { ThreadColor } from '../engine/model/types';
import type { Vec } from '../engine/geometry/vec';

export const UNITS_PER_MM = 10;

export interface MachineStitch {
  x: number;
  y: number;
  cmd: Command;
}

export interface MachinePattern {
  name: string;
  stitches: MachineStitch[];
  /** Thread per colour block. */
  threads: ThreadColor[];
}

export interface FormatLimits {
  /** Max |dx| and |dy| of a single stitch, machine units. */
  maxStitch: number;
  /** Max |dx| and |dy| of a single jump record, machine units. */
  maxJump: number;
  /** Max absolute coordinate the format can store, machine units (per axis). */
  maxCoord: number;
}

export const mmToUnits = (mm: number): number => Math.round(mm * UNITS_PER_MM);
export const unitsToMm = (u: number): number => u / UNITS_PER_MM;

/** Splits a move into equal integer steps that each respect `max` on both axes. */
function splitMove(from: Vec, to: Vec, max: number): Vec[] {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const n = Math.max(1, Math.ceil(Math.max(Math.abs(dx), Math.abs(dy)) / max));
  const out: Vec[] = [];
  for (let k = 1; k <= n; k++) out.push({ x: from.x + Math.round((dx * k) / n), y: from.y + Math.round((dy * k) / n) });
  return out;
}

export function toMachinePattern(seq: StitchSequence, origin: Vec, limits: FormatLimits, name: string): MachinePattern {
  const out: MachineStitch[] = [];
  let cur = { x: 0, y: 0 };
  for (let i = 0; i < seq.count; i++) {
    const cmd = seq.cmds[i] as Command;
    const target = { x: mmToUnits(seq.xs[i] - origin.x), y: mmToUnits(seq.ys[i] - origin.y) };
    if (cmd === CMD.STITCH || cmd === CMD.JUMP) {
      const max = cmd === CMD.STITCH ? limits.maxStitch : limits.maxJump;
      for (const p of splitMove(cur, target, max)) out.push({ x: p.x, y: p.y, cmd });
      cur = target;
    } else if (cmd === CMD.END) {
      out.push({ x: cur.x, y: cur.y, cmd });
      break;
    } else {
      out.push({ x: cur.x, y: cur.y, cmd });
    }
  }
  if (!out.length || out[out.length - 1].cmd !== CMD.END) out.push({ x: cur.x, y: cur.y, cmd: CMD.END });
  return { name, stitches: out, threads: seq.threads.slice() };
}

export interface Extents {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/** Extents of all stitch and jump positions (machine units). Empty patterns give zeros. */
export function extents(stitches: MachineStitch[]): Extents {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const s of stitches) {
    if (s.cmd !== CMD.STITCH && s.cmd !== CMD.JUMP) continue;
    if (s.x < minX) minX = s.x;
    if (s.y < minY) minY = s.y;
    if (s.x > maxX) maxX = s.x;
    if (s.y > maxY) maxY = s.y;
  }
  if (minX === Infinity) return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
  return { minX, minY, maxX, maxY };
}

export function countCommands(stitches: MachineStitch[], cmd: Command): number {
  let n = 0;
  for (const s of stitches) if (s.cmd === cmd) n++;
  return n;
}

/** Splits a pattern into colour blocks at COLOR_CHANGE commands (the change belongs to the next block). */
export function colorBlocks(stitches: MachineStitch[]): MachineStitch[][] {
  const blocks: MachineStitch[][] = [[]];
  for (const s of stitches) {
    if (s.cmd === CMD.COLOR_CHANGE) blocks.push([]);
    blocks[blocks.length - 1].push(s);
  }
  return blocks;
}
